import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";

export type Control = {
  id: string;
  component: ReactNode;
  anchorOffset?: Partial<Pick<DOMRect, "left" | "top">>;
  key?: string;
};

type UseBar = (getControl: () => Control | null) => void;

export type AppContext = {
  headerBar: {
    renderPrefix: () => ReactNode;
    renderSuffix: () => ReactNode;
  };
  viewBar: Control | null;
  setViewBar: (control: Control | null) => void;
  clearViewBar: (id: string) => void;

  toolBar: Control | null;
  setToolBar: (control: Control | null) => void;

  actionBar: Control | null;
  setActionBar: (control: Control | null) => void;
};

export const AppContext = createContext<AppContext | undefined>(undefined);

AppContext.displayName = "AppContext";

const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) throw new Error(`Expected AppContextProvider in ancestor tree.`);

  return context;
};

export const useHeaderBarContext = () => useAppContext().headerBar;

export const useApplicationControlBars = () => {
  const { actionBar, toolBar, viewBar } = useAppContext();

  return useMemo(() => ({ actionBar, toolBar, viewBar }), [actionBar, toolBar, viewBar]);
};

export const useViewBar: UseBar = (getControl) => {
  const { setViewBar, clearViewBar } = useAppContext();

  useEffect(() => {
    const control = getControl();
    setViewBar(control);

    return () => {
      if (control) clearViewBar(control.id);
    };
  }, [clearViewBar, getControl, setViewBar]);
};

export const useToolBar: UseBar = (getControl) => {
  const { setToolBar } = useAppContext();

  useEffect(() => {
    setToolBar(getControl());

    return () => setToolBar(null);
  }, [getControl, setToolBar]);
};

export const useActionBar: UseBar = (getControl) => {
  const { setActionBar } = useAppContext();

  useEffect(() => {
    setActionBar(getControl());

    return () => setActionBar(null);
  }, [getControl, setActionBar]);
};
